import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { showToast } from '../../utils/toast';
import { handleErrors } from '../../utils/common';

import ROUTES from '../../routes';
import api from '../../config/axiosInstance';

const imageKeys = [
    'section_one_image'
];

const ContactCMS = () => {
    const { code } = useParams();
    const formRef = useRef(null);

    const [submitting, setSubmitting] = useState(false);

    const [formData, setFormData] = useState({
        section_one_title: "",
        section_two_title: "",
        section_two_button_text: "",
        section_two_button_link: "",

    });

    const [images, setImages] = useState({});
    const [previews, setPreviews] = useState({});
    const [activeTab, setActiveTab] = useState('section_one');

    const handleChange = e => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleImageChange = (e, key) => {
        const file = e.target.files[0];
        if (file) {
            setImages(prev => ({ ...prev, [key]: file }));
            setPreviews(prev => ({ ...prev, [key]: URL.createObjectURL(file) }));
        }
    };

    useEffect(() => {
        const fetchCMS = async () => {
            try {
                const res = await api.get(`/cms/${code}`);
                if (res.status === 200) {
                    const data = res.data.cms;

                    const previewObj = {};
                    imageKeys.forEach(key => {
                        if (data[key]) {
                            previewObj[key] = `${res.data.base_url}${data[key]}`;
                        }
                    });

                    setPreviews(previewObj);

                    const {
                        section_one_title,
                        section_two_title,
                        section_two_button_text,
                        section_two_button_link
                    } = data;

                    setFormData({
                        section_one_title,
                        section_two_title,
                        section_two_button_text,
                        section_two_button_link
                    });
                }
            } catch (err) {
                handleErrors(err);
            }
        };

        fetchCMS();

        if (formRef.current && window.$.fn.validate) {

            window.$.validator.addMethod(
                "ckeditorRequired",
                (value) =>
                    value.replace(/<[^>]*>/g, "").trim().length > 0,
                "This field is required"
            );

            window.$(formRef.current).validate({
                ignore: ".ck-editor *",
                rules: {
                    section_one_title: { required: true },
                    section_two_title: { required: true },
                    section_two_button_text: { required: true },
                    section_two_button_link: { required: true },
                },
                messages: {},
                errorElement: 'label',
                validClass: 'is-valid',
                errorClass: 'is-invalid text-danger',
                highlight: (element, errorClass, validClass) => {
                    $(element).addClass(errorClass).removeClass(validClass);
                },
                unhighlight: (element, errorClass, validClass) => {
                    $(element).removeClass(errorClass).addClass(validClass);
                }
            });
        }
    }, [code]);

    const handleFormSubmit = async () => {
        const $ = window.jQuery;
        const form = $(formRef.current);

        if (form.valid()) {
            const data = new FormData();
            Object.entries(formData).forEach(([key, value]) => data.append(key, value));
            Object.entries(images).forEach(([key, file]) => data.append(key, file));

            try {
                setSubmitting(true);
                const res = await api.put(`/cms/${code}`, data, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                showToast('success', res.data.message, 'Success');
            } catch (err) {
                handleErrors(err);
            } finally {
                setSubmitting(false);
            }
        }
    };

    const renderImageInput = (label, name) => (
        <div className="mb-3 col-lg-6 col-md-6 col-sm-6">
            <label>{label}</label>
            <input type="file" className="form-control" onChange={e => handleImageChange(e, name)} />
            {previews[name] && <img src={previews[name]} alt="preview" width={100} className="mt-2" />}
        </div>
    );

    const renderTextInput = (label, name) => (
        <div className="mb-3 col-lg-6 col-md-6 col-sm-6">
            <label className="form-label">{label}</label>
            <input
                type="text"
                className="form-control"
                name={name}
                value={formData[name]}
                onChange={handleChange}
            />
        </div>
    );

    const renderTabContent = () => {
        switch (activeTab) {
            case 'section_one':
                return (
                    <>
                        {renderTextInput('Title', 'section_one_title')}
                        {renderImageInput('Image', 'section_one_image')}
                    </>
                );
            case 'section_two':
                return (
                    <>
                        {renderTextInput('Title', 'section_two_title')}
                        {renderTextInput('Button Test', 'section_two_button_text')}
                        {renderTextInput('Button Link', 'section_two_button_link')}
                    </>
                );
            default:
                return null;
        }
    };

    return (
        <>
            <div className="page-titles">
                <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                        <Link to={ROUTES.ROOT}>Dashboard</Link>
                    </li>
                    <li className="breadcrumb-item active">
                        <Link to={`/cms/contact`}>Contact Page</Link>
                    </li>
                </ol>
            </div>
            
            <div className="card">
                <div className="card-header">
                    <h4 className="card-title">Contact CMS</h4>
                </div>
                <div className="card-body">
                    <ul className="nav nav-tabs mb-3">
                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === 'section_one' ? 'active' : ''}`}
                                onClick={() => setActiveTab('section_one')}
                            >
                                Section One
                            </button>
                        </li>
                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === 'section_two' ? 'active' : ''}`}
                                onClick={() => setActiveTab('section_two')}
                            >
                                Section Two
                            </button>
                        </li>
                    </ul>
                    <form ref={formRef} noValidate>
                        <div className="row">{renderTabContent()}</div>
                        <div className="pt-3">
                            <button 
                                type="button"
                                onClick={handleFormSubmit}
                                className="btn btn-sm btn-primary me-2"
                                disabled={submitting}
                            >
                                {submitting ? (
                                    <>
                                        <i className="spinner-border spinner-border-sm me-1"></i>
                                        Submit
                                    </>
                                ) : (
                                    <>
                                        <i className="fa fa-floppy-disk me-1"></i>
                                        Submit
                                    </>
                                )}
                            </button>
                            <Link to={ROUTES.ROOT} className="btn btn-sm btn-danger light">
                                <i className="fas fa-times-circle"></i>&nbsp;Cancel
                            </Link>
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
};

export default ContactCMS;
